/**
 * Onboarding API client for optra-ai.
 *
 * Provides functions for checking onboarding status and triggering
 * provisioning for new users in SaaS mode.
 */

import {
  backendApi,
  type OnboardingStatusResponse,
  type OnboardingCompleteResponse,
  type OrganizationOption,
} from './api/backend'
import type { LucideIcon } from 'lucide-react'
import {
  Mail,
  ClipboardCheck,
  FileCode,
  Building,
  Users,
  Briefcase,
  Settings,
  CheckCircle,
} from 'lucide-react'

// Re-export types from backend for convenience
export type { OnboardingStatusResponse, OnboardingCompleteResponse, OrganizationOption }
/** @deprecated Use OnboardingCompleteResponse instead */
export type QuickProvisionResponse = OnboardingCompleteResponse

// ============================================================================
// Onboarding Step Types (matches backend OnboardingStep enum)
// ============================================================================

/**
 * Onboarding step identifier matching backend enum.
 * Used for tracking which step failed and showing progress.
 */
export type OnboardingStep =
  | 'validate_email'
  | 'check_status'
  | 'load_template'
  | 'create_organization'
  | 'create_team'
  | 'create_workspace'
  | 'create_gateway_config'
  | 'complete_onboarding'

/**
 * Step definition for the progress UI.
 */
export interface OnboardingStepInfo {
  id: OnboardingStep
  label: string
  description: string
  icon: LucideIcon
  /** Simulated duration in ms for frontend animation */
  simulatedDurationMs: number
}

/**
 * Ordered list of onboarding steps with UI metadata.
 * Used by ProvisioningProgress component to show animated progress.
 */
export const ONBOARDING_STEPS: OnboardingStepInfo[] = [
  {
    id: 'validate_email',
    label: 'Validating email',
    description: 'Verifying your company email address',
    icon: Mail,
    simulatedDurationMs: 300,
  },
  {
    id: 'check_status',
    label: 'Checking account',
    description: 'Checking your account status',
    icon: ClipboardCheck,
    simulatedDurationMs: 200,
  },
  {
    id: 'load_template',
    label: 'Loading configuration',
    description: 'Loading gateway configuration template',
    icon: FileCode,
    simulatedDurationMs: 200,
  },
  {
    id: 'create_organization',
    label: 'Creating organization',
    description: 'Setting up your organization',
    icon: Building,
    simulatedDurationMs: 400,
  },
  {
    id: 'create_team',
    label: 'Creating team',
    description: 'Setting up your team',
    icon: Users,
    simulatedDurationMs: 300,
  },
  {
    id: 'create_workspace',
    label: 'Creating workspace',
    description: 'Setting up your workspace',
    icon: Briefcase,
    simulatedDurationMs: 300,
  },
  {
    id: 'create_gateway_config',
    label: 'Configuring gateway',
    description: 'Configuring AI gateway settings',
    icon: Settings,
    simulatedDurationMs: 400,
  },
  {
    id: 'complete_onboarding',
    label: 'Completing setup',
    description: 'Finalizing your setup',
    icon: CheckCircle,
    simulatedDurationMs: 200,
  },
]

/**
 * Get step index by ID.
 */
export function getStepIndex(stepId: OnboardingStep): number {
  return ONBOARDING_STEPS.findIndex((s) => s.id === stepId)
}

/**
 * Get step info by ID.
 */
export function getStepInfo(stepId: OnboardingStep): OnboardingStepInfo | undefined {
  return ONBOARDING_STEPS.find((s) => s.id === stepId)
}

// ============================================================================
// Error Response Types
// ============================================================================

/**
 * Error response from onboarding endpoints with step information.
 */
export interface OnboardingErrorResponse {
  error: {
    code: number
    message: string
    step: OnboardingStep
    isRetryable: boolean
  }
}

/**
 * Check if an error response is an OnboardingErrorResponse.
 */
export function isOnboardingErrorResponse(data: unknown): data is OnboardingErrorResponse {
  if (!data || typeof data !== 'object') return false
  const obj = data as Record<string, unknown>
  if (!obj.error || typeof obj.error !== 'object') return false
  const error = obj.error as Record<string, unknown>
  return (
    typeof error.message === 'string' &&
    typeof error.step === 'string' &&
    typeof error.isRetryable === 'boolean'
  )
}

/**
 * Check onboarding status for the current user.
 *
 * Returns whether the user has completed onboarding.
 */
export async function getOnboardingStatus(): Promise<OnboardingStatusResponse> {
  return backendApi.onboarding.getStatus()
}

/**
 * Complete onboarding for the current user.
 *
 * This will:
 * 1. Validate that the user has a company email (blocks personal emails)
 * 2. Create an organization (user becomes owner)
 * 3. Create a default team
 * 4. Create a default workspace
 * 5. Create a gateway config with optimized settings
 * 6. Auto-assign all supported providers
 * 7. Mark onboarding as complete
 *
 * @param organizationName - Optional organization name. If not provided, derived from email domain.
 * @throws Error if the user has a personal email or is already onboarded.
 */
export async function completeOnboarding(organizationName?: string): Promise<OnboardingCompleteResponse> {
  return backendApi.onboarding.complete({ organizationName })
}

/** @deprecated Use completeOnboarding instead */
export async function quickProvision(organizationName?: string): Promise<OnboardingCompleteResponse> {
  return completeOnboarding(organizationName)
}

/**
 * Input for provisioning with organization selection.
 */
export interface ProvisionWithOrgInput {
  /** Use an existing organization by ID (mutually exclusive with createOrganization) */
  organizationId?: string
  /** Create a new organization (mutually exclusive with organizationId) */
  createOrganization?: { name: string }
}

/**
 * Provision with a specific organization selection.
 *
 * This is used when the user has multiple organizations and needs to select which
 * one to use, or wants to create a new organization.
 *
 * @param input - Either `organizationId` to use an existing org, or `createOrganization` to create new.
 * @throws Error if validation fails or neither/both options are provided.
 */
export async function provisionWithOrg(input: ProvisionWithOrgInput): Promise<OnboardingCompleteResponse> {
  if (input.createOrganization) {
    return backendApi.onboarding.complete({ organizationName: input.createOrganization.name })
  }
  // TODO: Add provisionWithOrg endpoint to backend for selecting existing org
  return backendApi.onboarding.complete({})
}
